interface ColorbarProps {
  palette: [number, string][];
  /**
   * Reference tick values that define the visual scale.
   * Each consecutive pair of ticks gets equal visual width regardless of
   * the data-unit gap between them — matching windy's log-like appearance.
   * ticks[0] is rendered as the unit label (e.g. "m"); ticks[1..] are values.
   */
  ticks: number[];
  /** Unit label shown at the left tick position (e.g. "m", "m/s") */
  unit: string;
}

export function Colorbar({ palette, ticks, unit }: ColorbarProps) {
  const nIntervals = ticks.length - 1;

  /**
   * Map a data value to a CSS percentage using the ticks as equally-spaced
   * reference breakpoints (piecewise linear within each tick interval).
   */
  const dataToCssPct = (v: number): number => {
    if (v <= ticks[0]) return 0;
    if (v >= ticks[nIntervals]) return 100;
    for (let i = 0; i < nIntervals; i++) {
      if (v >= ticks[i] && v < ticks[i + 1]) {
        const t = (v - ticks[i]) / (ticks[i + 1] - ticks[i]);
        return ((i + t) / nIntervals) * 100;
      }
    }
    return 100;
  };

  const gradientStops = palette
    .map(([v, c]) => `${c} ${dataToCssPct(v).toFixed(2)}%`)
    .join(", ");

  const labelStyle: React.CSSProperties = {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: 11,
    fontWeight: 500,
    color: "#fff",
    textShadow: "0 1px 3px rgba(0,0,0,0.8)",
    whiteSpace: "nowrap",
    lineHeight: 1,
  };

  return (
    <div
      style={{
        position: "absolute",
        bottom: 24,
        right: 12,
        width: 240,
        height: 24,
        borderRadius: 999,
        background: `linear-gradient(to right, ${gradientStops})`,
        boxShadow: "0 2px 8px rgba(0,0,0,0.45)",
        pointerEvents: "none",
        userSelect: "none",
        zIndex: 10,
      }}
    >
      {/* Unit label at left edge (ticks[0] position) */}
      <span style={{ ...labelStyle, left: 8, fontWeight: 700 }}>{unit}</span>

      {/* Value labels at equally-spaced tick positions */}
      {ticks.slice(1).map((v, i) => {
        const pct = ((i + 1) / nIntervals) * 100;
        const xShift = pct < 8 ? "0%" : pct > 92 ? "-100%" : "-50%";
        return (
          <span
            key={v}
            style={{
              ...labelStyle,
              left: `${pct}%`,
              transform: `translate(${xShift}, -50%)`,
            }}
          >
            {v}
          </span>
        );
      })}
    </div>
  );
}
